/* ---------- CP-001's tags to IEC 81346-2 two-letter classes (#347; the owner's decision on #64, docs/DECISIONS.md) ----------
 * The old single-letter tags and the new ones, as data, and how a save made with the old ones is read with the new ones. The
 * save migration in packages/progress uses it (format 7); nothing else does, so the engine never knows a project's tags.
 * Self-contained on purpose: the migration imports this file alone, not CP-001's model. */

/** Each old tag and its new one. -PE, the earthing busbar, and -U1/-U2, the plate's and the door's earth studs, keep theirs here: later formats rename them (earthbar.ts, studs.ts). */
export const CP001_TAG_RENAME: Readonly<Record<string, string>> = {
  Q0: "QB1", Q1: "QA1", Q2: "QA2", F1: "FC1", F2: "FC2", T1: "TB1", X0: "XD0", X1: "XD1", X2: "XD2",
  S0: "SF0", S1: "SF1", S2: "SF2", P1: "PF1", P2: "PF2", M1: "MA1",
};
/** The control circuit page's elements that were named by their device's tag: their ids follow the tags. The others ("Q1a",
 *  "Q2h": a contact or the coil of a device) keep theirs. */
const ELEMENT_RENAME: Readonly<Record<string, string>> = { S0: "SF0", S1: "SF1", S2: "SF2", P1: "PF1", P2: "PF2" };

const OLD = Object.keys(CP001_TAG_RENAME).join("|");
/** A tag where it names a terminal ("Q2:A1", "X0:2", "Q0:2|Q0:4"): never a terminal's own name (P1:X1 keeps its X1, 2/T1 its T1). */
const REF = new RegExp(`(?<![:/\\w])(${OLD})(?=:)`, "g");
const refs = (s: string) => s.replace(REF, t => CP001_TAG_RENAME[t]);

/** Every tag in a value: a device's own "tag" by the map, a terminal reference wherever it stands (a value or a key), and nothing
 *  else. A bare "X1" is a lamp's terminal, "P1" a power page position, "F2" the 24 V breaker as a protection taken out: kept. */
function walk(v: any, key?: string): any {
  if (typeof v === "string") return key === "tag" && v in CP001_TAG_RENAME ? CP001_TAG_RENAME[v] : refs(v);
  if (Array.isArray(v)) return v.map(x => walk(x));
  if (v && typeof v === "object") return Object.fromEntries(Object.entries(v).map(([k, x]) => [refs(k), walk(x, k)]));
  return v;
}

/** A saved CP-001 project ({ model, ui }, the model as a JSON string or an object, as saved) with the new tags. */
export function renameCp001Save<T extends { model?: any; ui?: any }>(s: T): T {
  const asText = typeof s.model === "string", model = walk(asText ? JSON.parse(s.model) : s.model ?? null);
  const d = model?.design;
  if (d?.slots) d.slots = Object.fromEntries(Object.entries(d.slots).map(([k, id]: [string, any]) => [k, ELEMENT_RENAME[id] ?? id]));
  if (d?.drawing?.parts) d.drawing.parts = Object.fromEntries(Object.entries(d.drawing.parts).map(([id, p]) => [ELEMENT_RENAME[id] ?? id, p]));
  return { ...s, model: asText ? JSON.stringify(model, null, 2) : model, ui: walk(s.ui ?? {}) };
}
